import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Clock, FilePlus, Edit3, Copy, GitBranch, FileDown, Trash2, ArrowRight, CheckCircle2, XCircle, Send, FileText, Upload } from "lucide-react";

const EVENT_META = {
  created:           { icon: FilePlus,    label: "Created" },
  updated:           { icon: Edit3,       label: "Edited" },
  duplicated:        { icon: Copy,        label: "Duplicated" },
  variation_created: { icon: GitBranch,   label: "Variation created" },
  pdf_generated:     { icon: FileDown,    label: "PDF generated" },
  status_changed:    { icon: ArrowRight,  label: "Status changed" },
  imported_items:    { icon: Upload,      label: "Items imported" },
  deleted:           { icon: Trash2,      label: "Deleted" },
};

const STATUS_ICONS = {
  won:      { icon: CheckCircle2, color: "var(--success)" },
  approved: { icon: CheckCircle2, color: "var(--success)" },
  lost:     { icon: XCircle,      color: "var(--danger)" },
  rejected: { icon: XCircle,      color: "var(--danger)" },
  sent:     { icon: Send,         color: "var(--info)" },
  draft:    { icon: FileText,     color: "var(--text-muted)" },
};

function fmt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-AU", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function QuoteTimeline({ quoteId, refreshKey }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    if (!quoteId || quoteId === "new") { setEvents([]); setLoading(false); return; }
    setLoading(true);
    api.get(`/quotes/${quoteId}/events`)
      .then((r) => mounted && setEvents(r.data))
      .catch(() => mounted && setEvents([]))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [quoteId, refreshKey]);

  if (!quoteId || quoteId === "new") return null;

  return (
    <div className="surface" data-testid="quote-timeline">
      <div className="px-5 py-4 border-b flex items-center gap-2" style={{ borderColor: "var(--border-subtle)" }}>
        <Clock className="w-3.5 h-3.5" style={{ color: "var(--accent)" }} />
        <div className="label-eyebrow">Activity</div>
        <div className="ml-auto text-xs font-mono" style={{ color: "var(--text-faint)" }}>{events.length}</div>
      </div>
      <div className="max-h-96 overflow-y-auto">
        {loading && <div className="p-6 text-xs font-mono" style={{ color: "var(--text-muted)" }}>Loading…</div>}
        {!loading && events.length === 0 && (
          <div className="p-6 text-xs" style={{ color: "var(--text-muted)" }}>No activity yet.</div>
        )}
        {!loading && events.map((ev) => {
          const meta = EVENT_META[ev.kind] || EVENT_META.updated;
          const Icon = meta.icon;
          return (
            <div
              key={ev.id}
              className="px-5 py-3 flex gap-3 border-b last:border-b-0"
              style={{ borderColor: "var(--border-subtle)" }}
              data-testid={`event-${ev.kind}`}
            >
              <div className="mt-0.5" style={{ color: "var(--text-muted)" }}>
                <Icon className="w-3.5 h-3.5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-sm flex-wrap">
                  <span className="font-medium" style={{ color: "var(--text-primary)" }}>{meta.label}</span>
                  {ev.kind === "status_changed" && <StatusChange data={ev.data} />}
                  {ev.kind === "duplicated" && ev.data?.new_quote_number && (
                    <span className="text-xs font-mono" style={{ color: "var(--text-muted)" }}>→ {ev.data.new_quote_number}</span>
                  )}
                  {ev.kind === "variation_created" && ev.data?.new_quote_number && (
                    <span className="text-xs font-mono text-gradient">→ {ev.data.new_quote_number}</span>
                  )}
                  {ev.kind === "updated" && typeof ev.data?.total === "number" && (
                    <span className="text-xs font-mono" style={{ color: "var(--text-faint)" }}>
                      total ${(ev.data.total).toLocaleString("en-AU", { minimumFractionDigits: 2 })}
                    </span>
                  )}
                </div>
                {ev.data?.lost_reason && (
                  <div className="text-xs mt-1 italic" style={{ color: "#FECDD3" }}>"{ev.data.lost_reason}"</div>
                )}
                <div className="text-[11px] font-mono mt-0.5" style={{ color: "var(--text-faint)" }}>
                  {ev.user_name || "system"} · {fmt(ev.created_at)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatusChange({ data }) {
  const from = data?.from || "—";
  const to = data?.to || "—";
  const fromMeta = STATUS_ICONS[from] || STATUS_ICONS.draft;
  const toMeta = STATUS_ICONS[to] || STATUS_ICONS.draft;
  const FromIcon = fromMeta.icon;
  const ToIcon = toMeta.icon;
  return (
    <span className="flex items-center gap-1.5 text-xs font-mono">
      <span className="flex items-center gap-1" style={{ color: fromMeta.color }}>
        <FromIcon className="w-3 h-3" /> {from}
      </span>
      <ArrowRight className="w-3 h-3" style={{ color: "var(--text-faint)" }} />
      <span className="flex items-center gap-1 font-bold" style={{ color: toMeta.color }}>
        <ToIcon className="w-3 h-3" /> {to}
      </span>
    </span>
  );
}
